import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { WarehouseType } from '../enum/warehouse-type.enum';

export interface WarehouseResponse {
  warehouses_type: WarehouseType;
  warehouse: {
    id: number;
    location: string;
    phone: string;
    website: string | null;
    facebook_link: string | null;
    instagram_link: string | null;
    tiktok_link: string | null;
    name: string;
    name_en: string;
    image_url: string | null;
    image: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class WarehouseTypeService {
  isAr: boolean = localStorage.getItem('language') == 'ar';
  private currentWarehouseResponse: WarehouseResponse | null = null;
  private warehouseTypeRequests = new Map<number, Observable<WarehouseResponse>>();

  constructor(private http: HttpClient) { }

  // ===============================
  // State
  // ===============================
  private _warehouseType$ = new BehaviorSubject<WarehouseType | null>(null);
  warehouseType$ = this._warehouseType$.asObservable();

  private _warehouseName$ = new BehaviorSubject<string>('');
  warehouseName$ = this._warehouseName$.asObservable();

  private _warehouseImage$ = new BehaviorSubject<string>('');
  warehouseImage$ = this._warehouseImage$.asObservable();

  private _warehouseId$ = new BehaviorSubject<number | null>(null);
  warehouseId$ = this._warehouseId$.asObservable();

  private _warehouseLocation$ = new BehaviorSubject<string>('');
  warehouseLocation$ = this._warehouseLocation$.asObservable();

  private _warehousePhone$ = new BehaviorSubject<string>('');
  warehousePhone$ = this._warehousePhone$.asObservable();

  private _warehouseWebsite$ = new BehaviorSubject<string | null>(null);
  warehouseWebsite$ = this._warehouseWebsite$.asObservable();

  private _warehouseFacebookLink$ = new BehaviorSubject<string | null>(null);
  warehouseFacebookLink$ = this._warehouseFacebookLink$.asObservable();

  private _warehouseInstagramLink$ = new BehaviorSubject<string | null>(null);
  warehouseInstagramLink$ = this._warehouseInstagramLink$.asObservable();

  private _warehouseTiktokLink$ = new BehaviorSubject<string | null>(null);
  warehouseTiktokLink$ = this._warehouseTiktokLink$.asObservable();

  get currentWarehouseType(): WarehouseType | null {
    return this._warehouseType$.value;
  }

  get currentWarehouseId(): number | null {
    return this._warehouseId$.value;
  }

  // ===============================
  // API
  // ===============================

  getWarehouseType(id: number): Observable<WarehouseResponse> {
    this.flowLog('getWarehouseType called', { requestedId: id });
    if (!Number.isFinite(id) || id <= 0) {
      this.resetWarehouseContext();
      this.flowLog('getWarehouseType invalid id', { requestedId: id });
      return of(this.getEmptyWarehouseResponse());
    }

    if (this.currentWarehouseId === id && this.currentWarehouseResponse) {
      this.flowLog('getWarehouseType cache hit', {
        requestedId: id,
        warehousesType: this.currentWarehouseResponse.warehouses_type
      });
      return of(this.currentWarehouseResponse);
    }

    const existingRequest = this.warehouseTypeRequests.get(id);

    if (existingRequest) {
      this.flowLog('getWarehouseType existing request reused', { requestedId: id });
      return existingRequest;
    }

    this.flowLog('getWarehouseType api request start', { requestedId: id });
    const request = this.http
      .get<WarehouseResponse>(`${environment.Server_URL}/warehouse/${id}/type`)
      .pipe(
        tap((res) => {
          this.flowLog('getWarehouseType api response normalized', {
            requestedId: id,
            responseWarehouseId: res?.warehouse?.id,
            warehousesType: res?.warehouses_type,
            name: res?.warehouse?.name,
            nameEn: res?.warehouse?.name_en,
            imageUrl: res?.warehouse?.image_url,
            rawWarehouseKeys: Object.keys(res?.warehouse ?? {})
          });
          this.currentWarehouseResponse = res;
          this._warehouseId$.next(res.warehouse.id);
          this._warehouseName$.next(this.isAr ? res.warehouse.name : res.warehouse.name_en);
          this._warehouseImage$.next(res.warehouse.image_url ?? '');
          this._warehouseLocation$.next(res.warehouse.location);
          this._warehousePhone$.next(res.warehouse.phone);
          this._warehouseWebsite$.next(res.warehouse.website);
          this._warehouseFacebookLink$.next(res.warehouse.facebook_link);
          this._warehouseInstagramLink$.next(res.warehouse.instagram_link);
          this._warehouseTiktokLink$.next(res.warehouse.tiktok_link);

          const type = res.warehouses_type as WarehouseType;
          this._warehouseType$.next(type);
          this.flowLog('warehouse subjects updated', {
            currentWarehouseId: this.currentWarehouseId,
            currentWarehouseType: this.currentWarehouseType
          });
        }),
        finalize(() => {
          this.flowLog('getWarehouseType finalize', { requestedId: id });
          this.warehouseTypeRequests.delete(id);
        }),
        shareReplay(1)
      );

    this.warehouseTypeRequests.set(id, request);

    return request;
  }

  resetWarehouseContext(): void {
    this.flowLog('resetWarehouseContext start');
    this.currentWarehouseResponse = null;
    this.warehouseTypeRequests.clear();
    this._warehouseType$.next(null);
    this._warehouseName$.next('');
    this._warehouseImage$.next('');
    this._warehouseId$.next(null);
    this._warehouseLocation$.next('');
    this._warehousePhone$.next('');
    this._warehouseWebsite$.next(null);
    this._warehouseFacebookLink$.next(null);
    this._warehouseInstagramLink$.next(null);
    this._warehouseTiktokLink$.next(null);
    this.flowLog('resetWarehouseContext done');
  }

  private getEmptyWarehouseResponse(): WarehouseResponse {
    return {
      warehouses_type: null as unknown as WarehouseType,
      warehouse: {
        id: 0,
        location: '',
        phone: '',
        website: null,
        facebook_link: null,
        instagram_link: null,
        tiktok_link: null,
        name: '',
        name_en: '',
        image_url: null,
        image: ''
      }
    };
  }

  // ===============================
  // Manual Setter (optional)
  // ===============================

  setWarehouseType(type: WarehouseType) {
    this._warehouseType$.next(type);
  }

  // ===============================
  // Basic Helpers
  // ===============================

  isCompany(): boolean {
    return this.currentWarehouseType === WarehouseType.PetCompany;
  }

  isWarehouse(): boolean {
    return this.currentWarehouseType === WarehouseType.Warehouse;
  }

  isShop(): boolean {
    return this.currentWarehouseType === WarehouseType.Shop;
  }

  isClinic(): boolean {
    return this.currentWarehouseType === WarehouseType.Clinic;
  }

  isShopAndClinic(): boolean {
    return this.currentWarehouseType === WarehouseType.ShopAndClinic;
  }

  isShipping(): boolean {
    return this.currentWarehouseType === WarehouseType.Shipping;
  }

  // ===============================
  // Feature Helpers
  // ===============================

  canShowProducts(): boolean {
    return (
      this.currentWarehouseType === WarehouseType.Shop ||
      this.currentWarehouseType === WarehouseType.ShopAndClinic
    );
  }

  canShowServices(): boolean {
    return (
      this.currentWarehouseType === WarehouseType.Clinic ||
      this.currentWarehouseType === WarehouseType.ShopAndClinic
    );
  }

  canShowBoth(): boolean {
    return this.currentWarehouseType === WarehouseType.ShopAndClinic;
  }

  // Reactive versions
  canShowProducts$ = this.warehouseType$.pipe(
    map(type =>
      type === WarehouseType.Shop ||
      type === WarehouseType.ShopAndClinic
    )
  );

  canShowServices$ = this.warehouseType$.pipe(
    map(type =>
      type === WarehouseType.Clinic ||
      type === WarehouseType.ShopAndClinic
    )
  );

  canShowBoth$ = this.warehouseType$.pipe(
    map(type => type === WarehouseType.ShopAndClinic)
  );

  // ✅ BlueBox Main (id = 1)
  get isBlueBoxMain(): boolean {
    return this.currentWarehouseId === 1;
  }

  isBlueBoxMain$ = this.warehouseId$.pipe(
    map(id => id === 1)
  );

  private flowLog(step: string, extra: Record<string, any> = {}): void {}
}
